/*
 * Wrapper component to render a component within the request workspace item.
 *
 * This takes care of:
 *
 *  - fetching the request
 *  - threading the request from the store to the wrapped component
 */
import PropTypes from 'prop-types';
import React from 'react';
import {connect} from 'react-redux';

import {BenchlingState} from 'redux-stores/reduceApp';
import {BenchlingDispatch} from 'redux-stores/Store';
import {Request} from 'requests/RequestConstants';
import {openRequest} from 'requests/RequestReduxActions';

interface ExternalProps {
  requestId: string;
}

interface ConnectedProps {
  request: Request;
}

interface DispatchProps {
  openRequest: (requestId: string) => void;
}

export default function withRequestLoading<P extends ExternalProps>(
  WrappedComponent: React.ComponentType<P & ConnectedProps & DispatchProps>
): React.ComponentClass<P> {
  function mapStateToProps(state: BenchlingState, {requestId}: P): ConnectedProps {
    return {
      request: state.models.requests.inputs.requestsById[requestId],
    };
  }

  function mapDispatchToProps(dispatch: BenchlingDispatch): DispatchProps {
    return {
      openRequest(requestId: string): void {
        dispatch(openRequest(requestId));
      },
    };
  }

  class Component extends React.Component<P & ConnectedProps & DispatchProps> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    static propTypes: any = {
      requestId: PropTypes.string.isRequired,
      openRequest: PropTypes.func.isRequired,
    };
    componentDidMount(): void {
      this.props.openRequest(this.props.requestId);
    }

    render(): JSX.Element {
      return <WrappedComponent {...this.props} />;
    }
  }

  // @ts-expect-error propTypes confuses TypeScript; We're going to move away from HOCs anyway.
  return connect(mapStateToProps, mapDispatchToProps)(Component);
}
